/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
import queryString from 'querystring';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@mantine/core';
import Button from '../../components/elements/Button';
import ButtonLink from '../../components/elements/ButtonLink';
import Card from '../../components/elements/Card';
import DataTable from '../../components/elements/DataTable';
import ScrollToTop from '../../components/elements/ScrollToTop';
import ErrorHandler from '../../components/elements/ErrorHandler';
import Modal from '../../components/elements/Modal';
import Text from '../../components/fields/Text';
import { AppContext } from '../../contexts';
import { getUserData, setUserIdMyptmActivation } from '../../utils/storage';
import {
  activateMyptmManualActivation,
  fetchMyptmActivationList,
  getExportedLinkAjaMyptmActivation,
  setMyptmActivationListFilter,
  setSuccessAction,
} from './actions';
import styles from './styles.scoped.css';
import Tab from '../../components/elements/Tab/Tab';
import AktivasiMyPertaminaFilter from '../../components/fragments/AktivasiMyPertaminaFilter';
import IconPersonalCard from '../../components/icons/IconPersonalCard';
import { dynamicContent, status } from './constants';
import IconClose from '../../components/icons/IconClose';
import { thousand } from '../../utils/format';
import { useDebounce } from '../../utils/hooks';

function AktivasiMyPertamina() {
  const { setBreadcrumbs } = useContext(AppContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLaAdmin = getUserData()?.isLinkAjaAdmin;
  const query = queryString.parse(location.search.replace('?', ''));
  const {
    table: { data, meta, loading, filter = null, message },
    activation,
    download,
  } = useSelector((state) => state.myptmActivation);
  const {
    dataDetailUser
  } = useSelector((state) => state.pageBase);

  const [tabItem, setTabItem] = useState('all');
  const [selectedRows, setSelectedRows] = useState([]);
  const [search, setSearch] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [enableManual, setEnableManual] = useState(false);
  const [editedRows, setEditedRows] = useState([]);
  const [manualActivationMessage, setManualActivationMessage] = useState(
    'Masukkan MID LinkAja yang telah diregistrasikan.'
  );
  const debounceSearch = useDebounce(search, 500);

  const handleChangeTab = (val) => {
    const newFilter = { ...filter, status: val === 'all' ? '' : val };
    setSelectedRows([]);
    setEnableManual(false);
    setTabItem(val);
    dispatch(setMyptmActivationListFilter(newFilter));
    const newQuery = queryString.stringify({ ...query, ...newFilter });
    navigate(`?${newQuery}`);
  };
  const handleSelectedRow = (row) => {
    let rows = selectedRows.length ? selectedRows : data;
    rows = rows.map((item) => {
      const isChecked = item.checked && item.userId === row.userId;
      const isMatch = item.userId === row.userId;
      return {
        ...item,
        checked: isChecked ? false : isMatch ? true : Boolean(item.checked),
      };
    });
    setSelectedRows(rows);
  };
  const handleEnableManual = () => {
    const rows = selectedRows.filter((row) => row.checked);
    setEnableManual(true);
    setOpenModal(false);
    setSelectedRows(rows);
    setEditedRows(rows.map((row) => ({ ...row, mid: '' })));
  };
  const handleDownloadSelectedRows = () => {
    if (tabItem === 'PENDING') {
      const userId = selectedRows
        .filter((item) => item.checked)
        .map((item) => item.userId);
      dispatch(getExportedLinkAjaMyptmActivation(userId));
      return;
    }
    setOpenModal(true);
  };
  const handleActivationManual = () => {
    const activations = editedRows.map(({ userId, mid }) => ({ userId, mid }));
    dispatch(activateMyptmManualActivation(activations));
  };
  const handleBulkActivation = async () => {
    const userId = selectedRows
      .filter((item) => item.checked)
      .map((item) => item.userId);
    await setUserIdMyptmActivation(userId);
    await setOpenModal(false);
    await dispatch(setSuccessAction({ key: 'download', data: { userId } }));
    navigate('/merchant/aktivasi-mypertamina/bulk');
  };
  const handleColumnEdit = (row, value) => {
    const { userId } = row;
    const edited = editedRows.map((item) => ({
      ...item,
      mid: userId === item.userId ? value : item.mid,
    }));
    setSelectedRows(edited);
    setEditedRows(edited);
  };

  const fetchMyptmActivationData = (page = 1, initial = true) => {
    dispatch(
      fetchMyptmActivationList({ nextPage: page, search, filter, initial })
    );
  };

  const isSelectable = ['PENDING', 'ON_PROCESS'].includes(tabItem);
  const numOfselected = selectedRows.filter((item) => item.checked).length;
  let column = [
    { heading: 'Tanggal Status', value: 'updatedAt' },
    { heading: 'Nama Pangkalan LPG 3 Kg', value: 'storeName' },
    { heading: 'MID', value: 'midMap' },
    {
      heading: 'Status',
      value: ({ status: itemStatus }) => (
        <Badge
          className={styles.badge__status}
          color={dynamicContent[status[itemStatus.toLowerCase()]]?.color}
        >
          {itemStatus}
        </Badge>
      ),
    },
    { heading: 'MID LinkAja', value: 'mid', editable: enableManual },
    { heading: 'Petinjau', value: 'reviewer' },
    {
      className:
        tabItem === 'ACTIVE' || tabItem === 'all' ? '' : 'hidden__col',
      heading: 'Action',
      value: ({ userId, status }) =>
        status === 'Aktif' ? (
          <ButtonLink
            className={styles.button__detail}
            to={`/merchant/aktivasi-mypertamina/detail/${userId}`}
          >
            Detail
          </ButtonLink>
        ) : (
          '-'
        ),
    },
  ];
  const tabList = useMemo(
    () => [
      { label: 'Semua', value: 'all', info: '' },
      ...meta.summary
        .map((item) => ({
          label: item.activationStatus,
          value: status[item.activationStatus.toLowerCase()],
          info: item.total || '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [meta.summary]
  );
  // validation manual activation
  const isValidToActivateManual = editedRows.every((item) => Boolean(item.mid));
  const editedMid = isValidToActivateManual
    ? editedRows.map((item) => item.mid)
    : [];
  const isDuplicateMid = editedMid.filter(
    (item, index) => editedMid.indexOf(item) !== index
  ).length;

  useEffect(() => {
    fetchMyptmActivationData();
  }, [filter, debounceSearch]);

  useEffect(() => {
    if (isLaAdmin) {
      navigate('/registrasi-merchant');
    }
  }, [isLaAdmin]);

  useEffect(() => {
    setBreadcrumbs(['Aktivasi MyPertamina']);
    setTabItem(query?.status || 'all');
    setSearch(query?.search || '');
  }, []);

  useEffect(() => {
    let reload = null;
    if (activation.success || download.success) {
      reload = setTimeout(() => navigate(0), 3000);
    }
    return () => clearTimeout(reload);
  }, [activation.success, download.success]);

  useEffect(() => {
    if (isDuplicateMid) {
      setManualActivationMessage('MID tidak boleh sama');
    }else{
      setManualActivationMessage('Masukkan MID LinkAja yang telah diregistrasikan.');
    }
  }, [isDuplicateMid]);

  if (message) {
    return <ErrorHandler message={message} />;
  }

  return (
    <div className={styles.root}>
      <Tab
        data={tabList}
        defaultValue={query.status || tabItem}
        onChange={handleChangeTab}
      />
      <AktivasiMyPertaminaFilter />
      <Card className={styles['table-filter-container']}>
        <p>
          Menampilkan <strong>{thousand(meta.totalData)}</strong> data
        </p>
        <div className={styles['filter-wrap']}>
          <div className={styles.filters}>
            <Text
              className={styles['search-bar']}
              hideLabel
              inputProps={{
                placeholder: 'Cari data...',
                value: search,
                icon: 'ic-search.svg',
                onChange: (e) => setSearch(e.target.value),
              }}
            />
          </div>
        </div>
      </Card>
      {isSelectable && !enableManual && dataDetailUser?.userType === 'superadmin' &&(
        <div className={styles.section__dynamic}>
          <p>{dynamicContent[tabItem]?.info}</p>
          <Button
            disabled={!numOfselected || download.loading}
            onClick={handleDownloadSelectedRows}
          >
            <IconPersonalCard />
            <span style={{ marginLeft: '0.5rem' }}>
              {numOfselected && tabItem === 'PENDING' ? (
                <>
                  Unduh <strong>{numOfselected}</strong> Data
                </>
              ) : (
                dynamicContent[tabItem].button
              )}
            </span>
          </Button>
        </div>
      )}
      {enableManual && (
        <div className={styles.section__dynamic}>
          <p className={isDuplicateMid ? 'error__manual' : null}>{manualActivationMessage}</p>
          <div>
            <Button
              onClick={() => {
                setSelectedRows([]);
                setEnableManual(false);
              }}
              style="danger"
            >
              Batal
            </Button>
            <Button
              disabled={!isValidToActivateManual || isDuplicateMid}
              onClick={handleActivationManual}
            >
              <IconPersonalCard />
              <span style={{ marginLeft: '0.5rem' }}>Aktifkan</span>
            </Button>
          </div>
        </div>
      )}
      <Card className={styles.table}>
        <div className={styles['table-container']}>
          <DataTable
            className={styles.table}
            column={column}
            data={selectedRows.length ? selectedRows : data}
            endTreshould={500}
            isSelectable={isSelectable && !enableManual && dataDetailUser?.userType === 'superadmin'}
            loading={loading}
            meta={meta}
            onColumnEdit={handleColumnEdit}
            onEndReached={() => fetchMyptmActivationData(meta.page + 1, false)}
            onRefresh={fetchMyptmActivationData}
            onSelectedRow={handleSelectedRow}
            withIndex
          />
        </div>
      </Card>
      <ScrollToTop />
      <Modal
        className={styles.modal}
        onClose={() => setOpenModal(false)}
        open={openModal}
      >
        <Button
          className={styles.button__close}
          onClick={() => setOpenModal(false)}
        >
          <IconClose style={{ fill: '#25282B' }} />
        </Button>
        <div className={styles.modal__content}>
          <h4>Proses Aktivasi</h4>
          <p>Silahkan pilih metode untuk proses aktivasi pangkalan LPG 3 kg.</p>
        </div>
        <div className={styles.modal__footer}>
          <Button onClick={handleBulkActivation}>
            Bulk
          </Button>
          <Button onClick={handleEnableManual}>
            Input Manual
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default AktivasiMyPertamina;
